/**
 * S3 Service - Upload e Download direto do browser para S3 (MinIO-compatible)
 * 
 * Substitui o fluxo anterior que passava pelo backend Python via SSH/SFTP.
 * Usado por: cad_agendamento, cad_devolucao, agendamento_view_modal, devolucao_view_modal
 */
import { S3Client, PutObjectCommand, GetObjectCommand, ListObjectsV2Command } from '@aws-sdk/client-s3';
import { toast } from 'react-toastify';
import axios from 'axios';

// ═══════════════════════════════════════════════════════════
// Configuração S3
// ═══════════════════════════════════════════════════════════
const S3_ENDPOINT = process.env.REACT_APP_S3_ENDPOINT;
const S3_ACCESS_KEY = process.env.REACT_APP_S3_ACCESS_KEY;
const S3_SECRET_KEY = process.env.REACT_APP_S3_SECRET_KEY;
const S3_REGION = process.env.REACT_APP_S3_REGION || 'us-east-1';

export const S3_BUCKETS = {
    agendamento: process.env.REACT_APP_S3_BUCKET_AGENDAMENTO || 'agend-retirada',
    devolucao: process.env.REACT_APP_S3_BUCKET_DEVOLUCAO || 'agend-devolucao',
    comprovantes: process.env.REACT_APP_S3_BUCKET || 'comprovantes-avaria',
    documentos: process.env.REACT_APP_S3_BUCKET_DOCUMENTOS || 'documentos-mot-vist',
    declaracaoEmail: process.env.REACT_APP_S3_BUCKET_DECLARACAO_EMAIL || 'declaracao-email-particular',
};

const s3Client = new S3Client({
    region: S3_REGION,
    endpoint: S3_ENDPOINT,
    forcePathStyle: true,
    credentials: {
        accessKeyId: S3_ACCESS_KEY,
        secretAccessKey: S3_SECRET_KEY,
    },
});

// ═══════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════
const MESES_PT = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/**
 * Gera a estrutura de pastas no padrão atual: Ano/Mes/Dia/Identificador/arquivo
 */
const buildS3Key = (identifier, fileName) => {
    const now = new Date();
    const ano = now.getFullYear().toString();
    const mes = MESES_PT[now.getMonth()];
    const dia = now.getDate().toString().padStart(2, '0');
    return `${ano}/${mes}/${dia}/${identifier}/${fileName}`;
};

// ═══════════════════════════════════════════════════════════
// Upload
// ═══════════════════════════════════════════════════════════

/**
 * Faz upload de um único arquivo para S3.
 * @param {File} file - Objeto File do browser
 * @param {string} bucket - Nome do bucket
 * @param {string} identifier - Booking (agendamento) ou Container (devolução)
 * @param {boolean} [flatKey=false] - Se true, usa estrutura simples: identifier/filename (sem pastas de data)
 * @returns {Promise<{success: boolean, key?: string, error?: string}>}
 */
export const uploadFileToS3 = async (file, bucket, identifier, flatKey = false) => {
    try {
        const s3Key = flatKey ? `${identifier}/${file.name}` : buildS3Key(identifier, file.name);
        const arrayBuffer = await file.arrayBuffer();

        const command = new PutObjectCommand({
            Bucket: bucket,
            Key: s3Key,
            Body: arrayBuffer,
            ContentType: file.type || 'application/octet-stream',
        });

        await s3Client.send(command);
        return { success: true, key: s3Key };
    } catch (error) {
        console.error('Erro S3 upload:', error);
        return { success: false, error: error.message || 'Erro no upload S3' };
    }
};

/**
 * Registra o path do arquivo S3 na tabela operacional.arquivos via backend.
 * @param {string} s3Key - Key do arquivo no S3
 * @param {string} tipoRegistro - "agendamento" ou "devolucao"
 * @param {string} identifier - Booking ou Container
 * @param {string} token - JWT token
 * @param {string} parsedURL - URL base da API
 * @param {string} [bucket] - Nome do bucket (opcional)
 */
const registerS3File = async (s3Key, tipoRegistro, identifier, token, parsedURL, bucket) => {
    try {
        await axios.post(
            `${parsedURL}post/arquivos/register_s3`,
            {
                s3_key: s3Key,
                tipo_registro: tipoRegistro,
                identifier: identifier,
                bucket: bucket || null
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
    } catch (error) {
        console.error('Erro ao registrar arquivo no banco:', error);
        // Não lançar erro — o arquivo já está no S3, apenas o registro falhou
    }
};

/**
 * Faz upload de múltiplos arquivos para S3 e registra cada um no banco.
 * @param {File[]} files - Lista de arquivos
 * @param {string} bucket - Nome do bucket S3
 * @param {string} identifier - Booking ou Container
 * @param {string} tipoRegistro - "agendamento" ou "devolucao"
 * @param {string} token - JWT token
 * @param {string} parsedURL - URL base da API
 * @returns {Promise<{uploaded: number, failed: number, keys: string[], errors: string[]}>}
 */
export const uploadFilesToS3 = async (files, bucket, identifier, tipoRegistro, token, parsedURL) => {
    const results = { uploaded: 0, failed: 0, keys: [], errors: [] };

    for (const file of files) {
        const result = await uploadFileToS3(file, bucket, identifier);
        if (result.success) {
            results.uploaded++;
            results.keys.push(result.key);
            // Registrar o path S3 no banco de dados
            if (tipoRegistro && token && parsedURL) {
                await registerS3File(result.key, tipoRegistro, identifier, token, parsedURL, bucket);
            }
        } else {
            results.failed++;
            results.errors.push(`${file.name}: ${result.error}`);
        }
    }

    return results;
};

// ═══════════════════════════════════════════════════════════
// Download
// ═══════════════════════════════════════════════════════════

/**
 * Baixa um único arquivo do S3.
 * @param {string} bucket - Nome do bucket
 * @param {string} key - Chave (path) do arquivo no S3
 * @returns {Promise<{data: Uint8Array, fileName: string} | null>}
 */
export const downloadFileFromS3 = async (bucket, key) => {
    try {
        const command = new GetObjectCommand({ Bucket: bucket, Key: key });
        const response = await s3Client.send(command);
        const data = await response.Body.transformToByteArray();
        const fileName = key.split('/').pop();
        return { data, fileName };
    } catch (error) {
        console.error('Erro S3 download:', error);
        return null;
    }
};

/**
 * Lista todos os objetos sob um prefixo no S3.
 * @param {string} bucket - Nome do bucket
 * @param {string} prefix - Prefixo (pasta) para listar
 * @returns {Promise<string[]>} Lista de keys
 */
export const listS3Objects = async (bucket, prefix) => {
    try {
        const command = new ListObjectsV2Command({
            Bucket: bucket,
            Prefix: prefix,
        });
        const response = await s3Client.send(command);
        return (response.Contents || []).map(obj => obj.Key);
    } catch (error) {
        console.error('Erro S3 listObjects:', error);
        return [];
    }
};

/**
 * Baixa múltiplos arquivos do S3 e gera um ZIP para download no browser.
 * @param {string[]} keys - Lista de keys S3
 * @param {string} bucket - Nome do bucket
 * @param {string} zipFileName - Nome do arquivo ZIP gerado
 */
export const downloadFilesAsZip = async (keys, bucket, zipFileName) => {
    // Importar JSZip dinamicamente para não carregar se não necessário
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    let downloadedCount = 0;
    for (const key of keys) {
        const result = await downloadFileFromS3(bucket, key);
        if (result) {
            zip.file(result.fileName, result.data);
            downloadedCount++;
        }
    }

    if (downloadedCount === 0) {
        toast.error('Nenhum arquivo encontrado para download.');
        return false;
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', zipFileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
};

/**
 * Baixa um único arquivo do S3 diretamente (sem ZIP).
 * @param {string} bucket - Nome do bucket
 * @param {string} key - Key do arquivo no S3
 */
export const downloadSingleFile = async (bucket, key) => {
    const result = await downloadFileFromS3(bucket, key);
    if (!result) {
        toast.error('Arquivo não encontrado.');
        return false;
    }

    const blob = new Blob([result.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', result.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
};

export default s3Client;

/**
 * Busca as S3 keys (caminhos) de arquivos salvos no banco para um codAgendamento.
 * @param {string|number} codAgendamento
 * @param {string} token - JWT token
 * @param {string} parsedURL - URL base da API
 * @returns {Promise<string[]>} Lista de S3 keys
 */
export const getArquivosS3Keys = async (codAgendamento, token, parsedURL) => {
    try {
        const response = await axios.get(
            `${parsedURL}get/arquivos/s3_keys`,
            {
                params: { codAgendamento },
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response.data?.keys || [];
    } catch (error) {
        console.error('Erro ao buscar keys de arquivos:', error);
        return [];
    }
};